/**
 * Publication categories — list, create, edit, deactivate.
 *
 * A category is DATA: target audience, intake schema, validation rules and SLA
 * all live in the row. That is what lets a third category exist without a
 * deploy, so this screen edits the whole record, not a curated subset.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { api, type Categoria, type Item, type Lista } from '../api/cliente'
import {
  Alternador,
  AreaTexto,
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Modal,
  Tabela,
  Vazio,
  formatarData,
} from '../componentes/ui'

interface Formulario {
  slug: string
  nome: string
  descricao: string
  fundamentoLegal: string
  urlFundamento: string
  exigeRegularidade: boolean
  slaPublicacaoDias: number
  ordem: number
  ativa: boolean
}

const VAZIO: Formulario = {
  slug: '',
  nome: '',
  descricao: '',
  fundamentoLegal: '',
  urlFundamento: '',
  exigeRegularidade: false,
  slaPublicacaoDias: 5,
  ordem: 0,
  ativa: true,
}

/** Turns a name into a URL-safe slug, so the operator never types one by hand. */
function paraSlug(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

export function Categorias() {
  const qc = useQueryClient()
  const [modalAberto, setModalAberto] = useState(false)
  const [editando, setEditando] = useState<Categoria | null>(null)
  const [form, setForm] = useState<Formulario>(VAZIO)
  const [erro, setErro] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState<Categoria | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const salvar = useMutation({
    mutationFn: async (valores: Formulario) => {
      const corpo = {
        ...valores,
        urlFundamento: valores.urlFundamento || undefined,
        descricao: valores.descricao || undefined,
        fundamentoLegal: valores.fundamentoLegal || undefined,
      }
      return editando
        ? api.put<Item<Categoria>>(`/api/v1/admin/categorias/${editando.id}`, corpo)
        : api.post<Item<Categoria>>('/api/v1/admin/categorias', corpo)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categorias'] })
      fechar()
    },
    onError: (e: Error) => setErro(e.message),
  })

  const desativar = useMutation({
    mutationFn: (id: string) => api.delete<Item<Categoria>>(`/api/v1/admin/categorias/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categorias'] })
      setConfirmando(null)
    },
  })

  function abrirNovo() {
    setEditando(null)
    setForm(VAZIO)
    setErro(null)
    setModalAberto(true)
  }

  function abrirEdicao(c: Categoria) {
    setEditando(c)
    setForm({
      slug: c.slug,
      nome: c.nome,
      descricao: c.descricao ?? '',
      fundamentoLegal: c.fundamentoLegal ?? '',
      urlFundamento: c.urlFundamento ?? '',
      exigeRegularidade: c.exigeRegularidade,
      slaPublicacaoDias: c.slaPublicacaoDias,
      ordem: c.ordem,
      ativa: c.ativa,
    })
    setErro(null)
    setModalAberto(true)
  }

  function fechar() {
    setModalAberto(false)
    setEditando(null)
    setErro(null)
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    salvar.mutate(form)
  }

  const categorias = data?.dados ?? []

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Categorias de publicação"
        descricao="Cada categoria define seu público-alvo, regras de validação e prazo. Uma nova categoria não exige alteração de sistema."
        acoes={<Botao onClick={abrirNovo}>Nova categoria</Botao>}
        className="overflow-hidden"
      >
        {isLoading ? (
          <Carregando />
        ) : categorias.length === 0 ? (
          <Vazio
            titulo="Nenhuma categoria cadastrada"
            descricao="Comece cadastrando a primeira categoria de publicação."
            acao={<Botao onClick={abrirNovo}>Nova categoria</Botao>}
          />
        ) : (
          <Tabela>
            <Cabecalho colunas={['Categoria', 'Fundamento legal', 'SLA', 'Situação', 'Atualizada', '']} />
            <tbody className="divide-y divide-slate-100">
              {categorias.map((c) => (
                <tr key={c.id} className="transition hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{c.nome}</p>
                    <p className="font-mono text-xs text-slate-400">{c.slug}</p>
                  </td>
                  <td className="max-w-xs px-4 py-3">
                    <p className="truncate text-slate-600">{c.fundamentoLegal ?? '—'}</p>
                    {c.exigeRegularidade && (
                      <span className="mt-1 inline-block">
                        <Etiqueta tom="atencao">exige regularidade</Etiqueta>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600 tabular">
                    {c.slaPublicacaoDias} dias
                  </td>
                  <td className="px-4 py-3">
                    <Etiqueta tom={c.ativa ? 'sucesso' : 'neutro'}>
                      {c.ativa ? 'Ativa' : 'Desativada'}
                    </Etiqueta>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                    {formatarData(c.atualizadoEm)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Botao variante="fantasma" tamanho="sm" onClick={() => abrirEdicao(c)}>
                      Editar
                    </Botao>
                    {c.ativa && (
                      <Botao variante="fantasma" tamanho="sm" onClick={() => setConfirmando(c)}>
                        Desativar
                      </Botao>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Cartao>

      {/* ------------------------------------------------- criar / editar */}
      <Modal
        aberto={modalAberto}
        aoFechar={fechar}
        largura="lg"
        titulo={editando ? 'Editar categoria' : 'Nova categoria'}
        descricao={
          editando
            ? 'As alterações são registradas na trilha de auditoria.'
            : 'O identificador não poderá ser alterado depois.'
        }
      >
        <form onSubmit={aoEnviar} className="space-y-5">
          <Campo
            rotulo="Nome"
            required
            obrigatorio
            value={form.nome}
            onChange={(e) => {
              const nome = e.target.value
              // Ao criar, o slug acompanha o nome; ao editar, nunca muda.
              setForm((f) => ({ ...f, nome, slug: editando ? f.slug : paraSlug(nome) }))
            }}
            placeholder="Escolas — relação de concluintes"
          />

          <Campo
            rotulo="Identificador (slug)"
            required
            obrigatorio
            disabled={Boolean(editando)}
            value={form.slug}
            onChange={(e) => setForm((f) => ({ ...f, slug: paraSlug(e.target.value) }))}
            ajuda={
              editando
                ? 'O identificador é imutável: pedidos já emitidos apontam para ele.'
                : 'Gerado a partir do nome. Apenas letras minúsculas, números e hífen.'
            }
          />

          <AreaTexto
            rotulo="Descrição"
            value={form.descricao}
            onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            ajuda="Texto exibido ao cliente. Descreva a obrigação com precisão — evite prometer o que a lei não exige."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Fundamento legal"
              value={form.fundamentoLegal}
              onChange={(e) => setForm((f) => ({ ...f, fundamentoLegal: e.target.value }))}
              placeholder="Resolução CEE nº 44/2020, Art. 4º"
            />
            <Campo
              rotulo="Link da norma"
              type="url"
              value={form.urlFundamento}
              onChange={(e) => setForm((f) => ({ ...f, urlFundamento: e.target.value }))}
              placeholder="https://…"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Prazo de publicação (dias)"
              type="number"
              min={1}
              max={365}
              required
              value={form.slaPublicacaoDias}
              onChange={(e) =>
                setForm((f) => ({ ...f, slaPublicacaoDias: Number(e.target.value) || 1 }))
              }
              ajuda="SLA acordado a partir do pagamento."
            />
            <Campo
              rotulo="Ordem de exibição"
              type="number"
              min={0}
              value={form.ordem}
              onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) || 0 }))}
            />
          </div>

          <div className="space-y-4 rounded-lg bg-slate-50 p-4">
            <Alternador
              rotulo="Exige regularidade da instituição"
              ajuda="Bloqueia o envio quando a instituição não está credenciada. Use para categorias com exigência como a do Art. 5º da Res. CEE 44/2020."
              checked={form.exigeRegularidade}
              onChange={(v) => setForm((f) => ({ ...f, exigeRegularidade: v }))}
            />
            <Alternador
              rotulo="Categoria ativa"
              ajuda="Categorias desativadas somem para o cliente, mas o histórico é preservado."
              checked={form.ativa}
              onChange={(v) => setForm((f) => ({ ...f, ativa: v }))}
            />
          </div>

          {erro && <Aviso tom="perigo">{erro}</Aviso>}

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
            <Botao type="button" variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao type="submit" carregando={salvar.isPending}>
              {editando ? 'Salvar alterações' : 'Criar categoria'}
            </Botao>
          </div>
        </form>
      </Modal>

      {/* ------------------------------------------------------ desativar */}
      <Modal
        aberto={Boolean(confirmando)}
        aoFechar={() => setConfirmando(null)}
        titulo="Desativar categoria"
      >
        <p className="text-sm text-slate-600">
          A categoria <strong className="text-slate-900">{confirmando?.nome}</strong> deixará de
          aparecer para novos pedidos.
        </p>
        <Aviso tom="info">
          Nada é apagado: pedidos já emitidos continuam apontando para esta categoria, e o histórico
          de publicações é preservado. Você pode reativá-la depois.
        </Aviso>
        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setConfirmando(null)}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            carregando={desativar.isPending}
            onClick={() => confirmando && desativar.mutate(confirmando.id)}
          >
            Desativar
          </Botao>
        </div>
      </Modal>
    </div>
  )
}
